import { useMemo } from 'react'
import { IServiceProps } from '../Services/types'
import { wbkFormat } from '../../../admin/components/Form/utils/dateTime'
import { useBookingContext } from '../../providers/BookingFormProvider/BookingFormProvider'
import closeIcon from '../../../../public/images/close-icon-medium.png'
import { __ } from '@wordpress/i18n'
import { wbkFormatPrice } from '../../providers/BookingFormProvider/utils'
import { minutesToText } from '../../../admin/components/WebbaDataTable/utils'
import { useWording } from '../../hooks/useWording'
import * as locales from 'date-fns/locale'
import { useLocale } from '../../hooks/useLocale'

export const SidebarItem = ({
    id,
    label,
    quantity,
    price,
    places,
    onUpdate,
    duration,
}: IServiceProps) => {
    const wording = useWording()
    const {
        dateFormat,
        timeFormat,
        timezone,
        attrService,
        priceFormat,
        amountData,
    } = useBookingContext()

    const { locale } = useLocale()

    const dateFnsLocale = useMemo(() => {
        // Helper function to get locale from the available locales
        const getLocale = (localeCode: string) => {
            // Common locale mappings
            const localeMapping: { [key: string]: string } = {
                en: 'enUS',
                'en-US': 'enUS',
                'en-GB': 'enGB',
                es: 'es',
                'es-ES': 'es',
                fr: 'fr',
                'fr-FR': 'fr',
                de: 'de',
                'de-DE': 'de',
                it: 'it',
                'it-IT': 'it',
                pt: 'pt',
                'pt-PT': 'pt',
                'pt-BR': 'ptBR',
                ru: 'ru',
                'ru-RU': 'ru',
                ja: 'ja',
                'ja-JP': 'ja',
                ko: 'ko',
                'ko-KR': 'ko',
                zh: 'zhCN',
                'zh-CN': 'zhCN',
                'zh-TW': 'zhTW',
                ar: 'ar',
                'ar-SA': 'arSA',
                nl: 'nl',
                'nl-NL': 'nl',
                sv: 'sv',
                'sv-SE': 'sv',
                da: 'da',
                'da-DK': 'da',
                no: 'nb',
                nb: 'nb',
                fi: 'fi',
                'fi-FI': 'fi',
                pl: 'pl',
                'pl-PL': 'pl',
                cs: 'cs',
                'cs-CZ': 'cs',
                sk: 'sk',
                'sk-SK': 'sk',
                hu: 'hu',
                'hu-HU': 'hu',
                ro: 'ro',
                'ro-RO': 'ro',
                bg: 'bg',
                'bg-BG': 'bg',
                hr: 'hr',
                'hr-HR': 'hr',
                sr: 'sr',
                'sr-RS': 'sr',
                sl: 'sl',
                'sl-SI': 'sl',
                et: 'et',
                'et-EE': 'et',
                lv: 'lv',
                'lv-LV': 'lv',
                lt: 'lt',
                'lt-LT': 'lt',
                tr: 'tr',
                'tr-TR': 'tr',
                he: 'he',
                'he-IL': 'he',
                hi: 'hi',
                'hi-IN': 'hi',
                th: 'th',
                'th-TH': 'th',
                vi: 'vi',
                'vi-VN': 'vi',
                id: 'id',
                'id-ID': 'id',
                ms: 'ms',
                'ms-MY': 'ms',
                bn: 'bn',
                'bn-BD': 'bn',
                'bn-IN': 'bn',
            }

            // Try exact match first
            const mappedLocale = localeMapping[localeCode]
            if (mappedLocale && locales[mappedLocale as keyof typeof locales]) {
                return locales[mappedLocale as keyof typeof locales]
            }

            // Try language code only (e.g., 'en' from 'en-US')
            const languageCode = localeCode.split('-')[0]
            const mappedLanguage = localeMapping[languageCode]
            if (
                mappedLanguage &&
                locales[mappedLanguage as keyof typeof locales]
            ) {
                return locales[mappedLanguage as keyof typeof locales]
            }

            // Default fallback
            return locales.enUS
        }

        return getLocale(locale || 'en')
    }, [locale])
    const amount = useMemo(() => {
        const matchedItems = amountData.items.filter((item) => item.id === id)
        if (matchedItems.length > 0) {
            return matchedItems.reduce(
                (sum, item) => sum + (item.price || 0),
                0
            )
        }

        const numericPrice = Number(price)
        if (numericPrice > 0 && quantity > 0) {
            return numericPrice * quantity
        }

        return 0
    }, [amountData, id, price, quantity])

    return (
        <div className={'wbk_sidebar__items__item'}>
            <div className={'wbk_sidebar__items__item__info'}>
                <h4 className={'wbk_sidebar__items__item__title'}>
                    {quantity && quantity > 1 && `${quantity}x `}
                    {label}
                </h4>
                {places && !!places.length && (
                    <p className={'wbk_sidebar__items__item__subline'}>
                        <span>
                            {wording.duration ||
                                __('Duration', 'webba-booking-lite')}
                        </span>
                        :{' '}
                        {typeof duration !== 'undefined'
                            ? __(
                                  minutesToText(Number(duration), {
                                      h: wording.hour,
                                      min: wording.minute,
                                  })
                              )
                            : ''}
                    </p>
                )}
                {places && !!places.length ? (
                    <p className={'wbk_sidebar__items__item__subline'}>
                        {places.map(({ timeslot }) => (
                            <div>
                                {wbkFormat(timeslot, 'F j', timezone, {
                                    locale: dateFnsLocale,
                                })}
                                ,{' '}
                                {wbkFormat(timeslot, timeFormat, timezone, {
                                    locale: dateFnsLocale,
                                })}
                            </div>
                        ))}
                    </p>
                ) : (
                    <p className={'wbk_sidebar__items__item__subline'}>
                        <span>
                            {wording.no_time_selected ||
                                __('No time selected', 'webba-booking-lite')}
                        </span>
                    </p>
                )}
            </div>
            <div className={'wbk_sidebar__items__item__price'}>
                {(amount &&
                    amount > 0 &&
                    wbkFormatPrice(amount, priceFormat)) || (
                    <span>
                        {wording.free || __('Free', 'webba-booking-lite')}
                    </span>
                )}
            </div>
            {(!attrService || attrService === '0') && (
                <div
                    className={'wbk_sidebar__items__item__button-remove'}
                    onClick={() =>
                        onUpdate({
                            selected: false,
                        })
                    }
                >
                    <img
                        src={closeIcon}
                        alt={
                            wording.remove_item ||
                            __('Remove item', 'webba-booking-lite')
                        }
                    />
                </div>
            )}
        </div>
    )
}
